import type { EvaluationLine } from '../api/types';
import {
  DEFAULT_DEPTH,
  DEFAULT_MULTI_PV,
  ENGINE_BUILD,
  ENGINE_SCRIPT_URL,
} from './config';
import { readEngineSettings } from './settings';
import { mergeLines, optionCommands, parseBestMove, parseInfoLine } from './uci';

/**
 * A Stockfish WASM build driven over UCI in a Web Worker.
 *
 * The engine is a single serial resource: it can run exactly one search at a time,
 * and a `go` issued while a search is live is ignored. Everything here exists to make
 * that safe when the user is walking the DAG faster than the engine finishes —
 * `analyse` cancels the previous search, waits for its `bestmove`, and stamps every
 * search with a generation so a late `info` line from an abandoned position can never
 * be rendered against the new one.
 *
 * Results from this engine are **advisory and local**. They are never written to the
 * shared `engine_evaluation` cache and never feed deviation detection; that cache is
 * content-addressed by the server's pinned engine binary and must stay reproducible
 * (spec §5.4/§6.5, R-16, ADR-0015).
 */

/** The subset of `Worker` this driver needs, so tests can supply a fake. */
export interface EngineWorker {
  postMessage(message: string): void;
  addEventListener(type: 'message', listener: (event: { data: unknown }) => void): void;
  addEventListener(type: 'error', listener: (event: unknown) => void): void;
  terminate(): void;
}

export type EngineWorkerFactory = () => EngineWorker;

export interface AnalysisSnapshot {
  /** The position this snapshot describes; compare before rendering. */
  fen: string;
  lines: EvaluationLine[];
  /** Highest depth reported so far. */
  depth: number;
  /** True once the engine reported `bestmove` for this search. */
  complete: boolean;
}

export interface AnalyseOptions {
  depth?: number;
  multiPv?: number;
  /** Called on every improved snapshot, including partial depths. */
  onUpdate?: (snapshot: AnalysisSnapshot) => void;
}

export class EngineUnavailableError extends Error {}

function defaultWorkerFactory(): EngineWorker {
  return new Worker(ENGINE_SCRIPT_URL) as unknown as EngineWorker;
}

export class LocalEngine {
  private worker: EngineWorker | null = null;
  private readonly factory: EngineWorkerFactory;
  private readonly listeners = new Set<(line: string) => void>();
  private booted: Promise<void> | null = null;
  private disposed = false;
  private multiPv = DEFAULT_MULTI_PV;
  /** Hash size the engine was last told; the viewer's setting may move on from it. */
  private hashMb = readEngineSettings().hashMb;

  /** Monotonic search id; a snapshot from an older generation is discarded. */
  private generation = 0;
  /** Resolves when the in-flight search has reported `bestmove`. */
  private searchSettled: Promise<void> = Promise.resolve();
  private searching = false;

  constructor(factory: EngineWorkerFactory = defaultWorkerFactory) {
    this.factory = factory;
  }

  /** Boot the worker and complete the UCI handshake. Idempotent. */
  async init(): Promise<void> {
    if (this.disposed) throw new EngineUnavailableError('engine has been disposed');
    if (this.booted !== null) return this.booted;
    this.booted = this.boot();
    return this.booted;
  }

  private async boot(): Promise<void> {
    let worker: EngineWorker;
    try {
      worker = this.factory();
    } catch (cause) {
      throw new EngineUnavailableError(
        `could not start the ${ENGINE_BUILD} worker: ${String(cause)}`,
      );
    }
    this.worker = worker;

    worker.addEventListener('message', (event: { data: unknown }) => {
      const data = event.data;
      const text = typeof data === 'string' ? data : String(data);
      for (const line of text.split('\n')) {
        const trimmed = line.trim();
        if (trimmed !== '') this.emit(trimmed);
      }
    });
    worker.addEventListener('error', () => {
      // A worker-level error kills the session; further analyse() calls reject
      // rather than hanging on a handshake that will never complete.
      this.worker = null;
    });

    await this.expect('uciok', () => worker.postMessage('uci'));
    for (const command of optionCommands({ hashMb: this.hashMb, multiPv: this.multiPv })) {
      worker.postMessage(command);
    }
    worker.postMessage('ucinewgame');
    await this.expect('readyok', () => worker.postMessage('isready'));
  }

  private emit(line: string): void {
    for (const listener of [...this.listeners]) listener(line);
  }

  private onLine(listener: (line: string) => void): () => void {
    this.listeners.add(listener);
    return () => this.listeners.delete(listener);
  }

  /** Resolve once a line starting with `prefix` arrives, after running `send`. */
  private expect(prefix: string, send: () => void): Promise<void> {
    return new Promise<void>((resolve) => {
      const off = this.onLine((line) => {
        if (line.startsWith(prefix)) {
          off();
          resolve();
        }
      });
      send();
    });
  }

  /**
   * Analyse `fen`, cancelling any search already running.
   *
   * Resolves with the final snapshot when the search completes, or with the last
   * partial snapshot when a newer `analyse` superseded it — a superseded search is a
   * normal outcome (the user moved on), not an error.
   */
  async analyse(fen: string, options: AnalyseOptions = {}): Promise<AnalysisSnapshot> {
    await this.init();
    const worker = this.worker;
    if (worker === null) throw new EngineUnavailableError('the engine worker is not running');

    const depth = options.depth ?? DEFAULT_DEPTH;
    const multiPv = options.multiPv ?? DEFAULT_MULTI_PV;
    const generation = ++this.generation;

    // Take over the engine: ask the live search to stop, then wait for its bestmove.
    // Issuing `position`/`go` before that arrives would be dropped by the engine.
    if (this.searching) worker.postMessage('stop');
    await this.searchSettled;
    if (generation !== this.generation) {
      // Superseded while we waited for the previous search to unwind.
      return { fen, lines: [], depth: 0, complete: false };
    }

    // Resizing the hash clears it, so it is only sent between searches, when the
    // viewer actually changed the setting.
    const hashMb = readEngineSettings().hashMb;
    if (hashMb !== this.hashMb) {
      this.hashMb = hashMb;
      worker.postMessage(`setoption name Hash value ${hashMb}`);
    }

    if (multiPv !== this.multiPv) {
      this.multiPv = multiPv;
      worker.postMessage(`setoption name MultiPV value ${multiPv}`);
    }

    let snapshot: AnalysisSnapshot = { fen, lines: [], depth: 0, complete: false };
    this.searching = true;
    this.searchSettled = new Promise<void>((resolve) => {
      const off = this.onLine((line) => {
        if (generation !== this.generation && !line.startsWith('bestmove')) return;

        const info = parseInfoLine(line);
        if (info !== null) {
          snapshot = {
            fen,
            lines: mergeLines(snapshot.lines, info),
            depth: Math.max(snapshot.depth, info.depth),
            complete: false,
          };
          if (generation === this.generation) options.onUpdate?.(snapshot);
          return;
        }

        if (line.startsWith('bestmove')) {
          off();
          this.searching = false;
          const bestMove = parseBestMove(line);
          snapshot = {
            ...snapshot,
            complete: true,
            // Trust the engine's own `bestmove` over the last PV head: on a stopped
            // search the newest PV may belong to an unfinished iteration.
            lines: snapshot.lines.map((entry) =>
              entry.rank === 1 && bestMove !== null ? { ...entry, bestMove } : entry,
            ),
          };
          if (generation === this.generation) options.onUpdate?.(snapshot);
          resolve();
        }
      });

      worker.postMessage(`position fen ${fen}`);
      worker.postMessage(`go depth ${depth}`);
    });

    await this.searchSettled;
    return snapshot;
  }

  /** Stop the current search without tearing down the engine. */
  stop(): void {
    this.generation += 1;
    if (this.searching) this.worker?.postMessage('stop');
  }

  /** Terminate the worker. The instance cannot be reused afterwards. */
  dispose(): void {
    this.disposed = true;
    this.generation += 1;
    this.listeners.clear();
    this.worker?.terminate();
    this.worker = null;
    this.booted = null;
  }
}

let shared: LocalEngine | null = null;

/**
 * The process-wide engine.
 *
 * One WASM instance per tab: booting costs a multi-megabyte download plus NNUE
 * initialization, and a second instance would contend for the same CPU while
 * doubling memory for no benefit.
 */
export function getSharedEngine(factory?: EngineWorkerFactory): LocalEngine {
  if (shared === null) shared = new LocalEngine(factory);
  return shared;
}

/** Dispose and forget the shared engine (tests, and sign-out teardown). */
export function resetSharedEngine(): void {
  shared?.dispose();
  shared = null;
}
